import { type ReactElement } from "react";

import { Colors } from "@snapchat/snap-design-system";
import { CheckIcon, ErrorIcon, type IconProps, KeyframeFilledIcon } from "@snapchat/snap-design-system-icons";

import { MyLensesLensStatus } from "../../gql-generated/types";

export interface LensStatusDisplayMeta {
    name: string;
    color: Colors.ColorPalette;
    icon?: (props: IconProps) => ReactElement;
    isColorInverted?: boolean;
}

export const defaultStatusEntry: LensStatusDisplayMeta = {
    name: "N/A",
    color: Colors.Gray.V250,
    icon: KeyframeFilledIcon,
};

export const publishedStatusEntry: LensStatusDisplayMeta = {
    name: "Published",
    color: Colors.Green.V150,
    icon: CheckIcon,
};

export const detailsPageAvailableStatusEntry = {
    name: "Ready for Publishing",
    color: Colors.Green.V150,
    icon: CheckIcon,
    isColorInverted: true,
};

export const lensStatusMap: Record<MyLensesLensStatus, LensStatusDisplayMeta> = {
    [MyLensesLensStatus.LensStatusInReview]: { name: "In Review", color: Colors.Orange.V100, icon: KeyframeFilledIcon },
    [MyLensesLensStatus.LensStatusProcessing]: {
        name: "Processing",
        color: Colors.Orange.V100,
        icon: KeyframeFilledIcon,
    },
    [MyLensesLensStatus.LensStatusInvalid]: {
        name: "Invalid",
        color: Colors.Orange.V100,
        icon: ErrorIcon,
    },
    [MyLensesLensStatus.LensStatusReady]: { name: "Ready", color: Colors.Orange.V100, icon: CheckIcon },
    [MyLensesLensStatus.LensStatusFinalized]: { name: "Finalized", color: Colors.Green.V150, icon: CheckIcon },
    [MyLensesLensStatus.LensStatusRejected]: {
        name: "Rejected",
        color: Colors.Red.V150,
        icon: ErrorIcon,
    },
    [MyLensesLensStatus.LensStatusFinalizing]: {
        name: "Finalizing",
        color: Colors.Orange.V100,
        icon: KeyframeFilledIcon,
    },
    [MyLensesLensStatus.LensStatusLive]: {
        name: "Public",
        color: Colors.Green.V150,
        icon: CheckIcon,
    },
    [MyLensesLensStatus.LensStatusOffline]: { name: "Offline", color: Colors.Gray.V250, icon: KeyframeFilledIcon },
    [MyLensesLensStatus.LensStatusHidden]: { name: "Hidden", color: Colors.Purple.V150, icon: KeyframeFilledIcon },
    [MyLensesLensStatus.LensStatusAwaitingProductTagging]: {
        name: "Awaiting Product Tagging",
        color: Colors.Orange.V100,
        icon: KeyframeFilledIcon,
    },
    [MyLensesLensStatus.LensStatusNotPublishedToSnapchat]: { name: "Not Published", color: Colors.Gray.V300 },
    [MyLensesLensStatus.LensStatusAvailable]: { name: "Available", color: Colors.Gray.V300, icon: CheckIcon },
    [MyLensesLensStatus.LensStatusUnset]: defaultStatusEntry,
} as const;

type AvailableEndStatus = Exclude<
    MyLensesLensStatus,
    | MyLensesLensStatus.LensStatusProcessing
    | MyLensesLensStatus.LensStatusInReview
    | MyLensesLensStatus.LensStatusAwaitingProductTagging
    | MyLensesLensStatus.LensStatusRejected
    | MyLensesLensStatus.LensStatusUnset
    | MyLensesLensStatus.LensStatusInvalid
    | MyLensesLensStatus.LensStatusFinalizing
>;

const availableEndStatusesMap: Record<AvailableEndStatus, undefined> = {
    // using Record to enforce type checking
    [MyLensesLensStatus.LensStatusReady]: undefined,
    [MyLensesLensStatus.LensStatusFinalized]: undefined,
    [MyLensesLensStatus.LensStatusLive]: undefined,
    [MyLensesLensStatus.LensStatusOffline]: undefined,
    [MyLensesLensStatus.LensStatusHidden]: undefined,
    [MyLensesLensStatus.LensStatusNotPublishedToSnapchat]: undefined,
    [MyLensesLensStatus.LensStatusAvailable]: undefined,
} as const;

export const availableEndStatusesSet = new Set(Object.keys(availableEndStatusesMap));
